import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { messageLisible } from '@/shared/api/erreurs';
import { ecrireJetonRafraichissement } from '@/shared/api/jetons';
import { useSession } from '@/shared/auth/sessionStore';
import { Logo } from '@/shared/ui/Logo';
import { Bouton } from '@/shared/ui/Bouton';
import { ErreurChamp, Etiquette, Saisie } from '@/shared/ui/Champ';
import { Carte, ContenuCarte } from '@/shared/ui/Surface';
import { useEffect, useState } from 'react';

const schemaConnexion = z.object({
  email: z.email('Adresse e-mail invalide.'),
  motDePasse: z.string().min(1, 'Mot de passe requis.'),
});

const schemaInscription = z.object({
  email: z.email('Adresse e-mail invalide.'),
  // 12 caracteres minimum, aligne sur la politique de l'API (recommandation ANSSI/NIST :
  // privilegier la longueur a la complexite).
  motDePasse: z.string().min(12, 'Au moins 12 caracteres.'),
  prenom: z.string().trim().optional(),
  nom: z.string().trim().optional(),
});

/** Enveloppe commune aux ecrans d'authentification. */
/**
 * Cadre commun a la connexion et a l'inscription.
 *
 * LE LOGO OFFICIEL REMPLACE L'ANCIEN PICTOGRAMME. Un anneau dessine a la main
 * cohabitait ici avec le vrai lockup employe partout ailleurs : deux
 * representations concurrentes de la meme marque, et celle que voit un visiteur
 * en premier n'etait pas la bonne.
 *
 * Le titre textuel disparait avec lui : le lockup PORTE deja le nom, en toutes
 * lettres et dans la typographie de la charte. Le repeter dessous ferait doublon,
 * et un lecteur d'ecran l'annoncerait deux fois.
 */
function Cadre({
  titre,
  sousTitre,
  children,
}: {
  titre: string;
  sousTitre: string;
  children: React.ReactNode;
}) {
  return (
    <div className="fond-stellaire flex min-h-dvh items-center justify-center bg-fond px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <Logo taille="lg" />

          {/*
            Le `h1` reste, mais il nomme la PAGE — « Se connecter », « Creer un
            compte » — et non la marque, que le lockup porte deja. Sans lui, la page
            n'aurait plus de titre de premier niveau : c'est par la qu'un lecteur
            d'ecran se repere, et c'est aussi ce que verifient les tests de parcours.
          */}
          <h1 className="text-lg font-semibold tracking-tight text-texte">{titre}</h1>
          <p className="text-sm text-texte-doux">{sousTitre}</p>
        </div>

        <Carte>
          <ContenuCarte className="pt-5">{children}</ContenuCarte>
        </Carte>
      </div>
    </div>
  );
}

export default function ConnexionPage() {
  const connecter = useSession((etat) => etat.connecter);
  const restaurer = useSession((etat) => etat.restaurer);
  const naviguer = useNavigate();
  const emplacement = useLocation();
  const [parametres, definirParametres] = useSearchParams();
  const [erreur, setErreur] = useState<string | null>(
    parametres.get('erreur') === 'google'
      ? 'La connexion Google n’a pas abouti. Reessayez, ou utilisez votre mot de passe.'
      : null,
  );
  const [enCours, setEnCours] = useState(false);

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * RETOUR DE GOOGLE : ON ECHANGE LE PASSE-PLAT, PUIS ON EFFACE L'ADRESSE.
   *
   * L'API nous renvoie ici avec un jeton a usage unique. On l'echange tout de
   * suite contre une vraie session — l'echange le fait tourner, donc il ne vaut
   * plus rien ensuite — et on retire le parametre de la barre d'adresse pour
   * qu'il ne traine ni dans l'historique ni dans une capture d'ecran.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const passePlat = parametres.get('echange');

  useEffect(() => {
    if (!passePlat) return;

    ecrireJetonRafraichissement(passePlat);
    definirParametres({}, { replace: true });

    /*
     * UN MESSAGE DISTINCT DE CELUI DU REFUS.
     *
     * Les deux echecs — Google qui refuse, et l'echange du passe-plat qui rate —
     * affichaient la meme phrase. Impossible de savoir lequel s'etait produit, ni
     * donc de quel cote chercher. Ils se distinguent maintenant.
     */
    void restaurer().catch(() => {
      setErreur(
        'Google a bien repondu, mais la session n’a pas pu etre ouverte. Reessayez, ou utilisez votre mot de passe.',
      );
    });
  }, [passePlat, definirParametres, restaurer]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schemaConnexion>>({ resolver: zodResolver(schemaConnexion) });

  const retour = (emplacement.state as { retour?: string } | null)?.retour ?? '/';

  return (
    <Cadre titre="Se connecter" sousTitre="Connectez-vous a votre espace.">
      <form
        noValidate
        className="space-y-4"
        onSubmit={handleSubmit(async (valeurs) => {
          setErreur(null);
          setEnCours(true);

          try {
            await connecter(valeurs.email, valeurs.motDePasse);
            naviguer(retour, { replace: true });
          } catch (cause) {
            setErreur(messageLisible(cause));
          } finally {
            setEnCours(false);
          }
        })}
      >
        <div className="space-y-1.5">
          <Etiquette htmlFor="email">Adresse e-mail</Etiquette>
          <Saisie
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            {...register('email')}
          />
          <ErreurChamp>{errors.email?.message}</ErreurChamp>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <Etiquette htmlFor="motDePasse">Mot de passe</Etiquette>

            {/*
              LE LIEN EST ICI, PAS EN BAS DE PAGE. On le cherche au moment ou l'on
              bute sur ce champ ; l'ecran existait deja, mais rien n'y menait — il
              fallait connaitre l'URL pour reinitialiser son mot de passe.
            */}
            <Link
              className="text-xs text-accent underline-offset-4 hover:underline"
              to="/mot-de-passe-oublie"
            >
              Mot de passe oublie ?
            </Link>
          </div>

          <Saisie
            id="motDePasse"
            type="password"
            autoComplete="current-password"
            aria-invalid={!!errors.motDePasse}
            {...register('motDePasse')}
          />
          <ErreurChamp>{errors.motDePasse?.message}</ErreurChamp>
        </div>

        {erreur ? (
          <p role="alert" className="text-sm font-medium text-danger">
            {erreur}
          </p>
        ) : null}

        <Bouton type="submit" className="w-full" enCours={enCours}>
          Se connecter
        </Bouton>

        <BoutonGoogle libelle="Continuer avec Google" />

        <p className="text-center text-sm text-texte-doux">
          Pas encore de compte ?{' '}
          <Link to="/inscription" className="font-medium text-accent hover:underline">
            Creer un compte
          </Link>
        </p>
      </form>
    </Cadre>
  );
}

/**
 * Passer par Google, pour se connecter comme pour s'inscrire.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * UN SEUL BOUTON, PARCE QU'IL N'Y A QU'UN SEUL PARCOURS.
 *
 * Cote serveur, le retour Google cree le compte s'il n'existe pas, et rattache
 * l'identite a un compte deja ouvert sous la meme adresse. Inscription et
 * connexion aboutissent donc au meme endroit — deux boutons distincts
 * laisseraient croire a deux chemins, et finiraient par diverger.
 *
 * REDIRECTION PLEIN NAVIGATEUR, pas un appel en arriere-plan : l'echange
 * OpenID Connect a besoin de la barre d'adresse et des cookies du navigateur.
 * ═══════════════════════════════════════════════════════════════════════════
 */
function BoutonGoogle({ libelle }: { libelle: string }) {
  return (
    <Bouton
      type="button"
      variante="secondaire"
      className="w-full"
      onClick={() => {
        /*
         * `depart` — et non `google` directement.
         *
         * L'API commence par ramener le navigateur sur SA propre origine, pour
         * que le cookie de correlation d'OAuth soit pose et relu au meme endroit.
         * En developpement, front et API sont sur deux ports ; sans ce saut, le
         * cookie se perdait entre les deux et Google etait refuse au retour.
         */
        window.location.href = '/v1/auth/google/depart';
      }}
    >
      {libelle}
    </Bouton>
  );
}

export function InscriptionPage() {
  const inscrire = useSession((etat) => etat.inscrire);
  const naviguer = useNavigate();
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schemaInscription>>({ resolver: zodResolver(schemaInscription) });

  return (
    <Cadre titre="Creer un compte" sousTitre="Quelques secondes suffisent.">
      <form
        noValidate
        className="space-y-4"
        onSubmit={handleSubmit(async (valeurs) => {
          setErreur(null);
          setEnCours(true);

          try {
            await inscrire(valeurs);
            naviguer('/entreprise/nouvelle', { replace: true });
          } catch (cause) {
            setErreur(messageLisible(cause));
          } finally {
            setEnCours(false);
          }
        })}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Etiquette htmlFor="prenom">Prenom</Etiquette>
            <Saisie id="prenom" autoComplete="given-name" {...register('prenom')} />
          </div>

          <div className="space-y-1.5">
            <Etiquette htmlFor="nom">Nom</Etiquette>
            <Saisie id="nom" autoComplete="family-name" {...register('nom')} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Etiquette htmlFor="email">Adresse e-mail</Etiquette>
          <Saisie
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            {...register('email')}
          />
          <ErreurChamp>{errors.email?.message}</ErreurChamp>
        </div>

        <div className="space-y-1.5">
          <Etiquette htmlFor="motDePasse">Mot de passe</Etiquette>
          <Saisie
            id="motDePasse"
            type="password"
            autoComplete="new-password"
            aria-invalid={!!errors.motDePasse}
            {...register('motDePasse')}
          />
          <ErreurChamp>{errors.motDePasse?.message}</ErreurChamp>
        </div>

        {erreur ? (
          <p role="alert" className="text-sm font-medium text-danger">
            {erreur}
          </p>
        ) : null}

        <Bouton type="submit" className="w-full" enCours={enCours}>
          Creer mon compte
        </Bouton>

        <BoutonGoogle libelle="S’inscrire avec Google" />

        <p className="text-center text-sm text-texte-doux">
          Deja inscrit ?{' '}
          <Link to="/connexion" className="font-medium text-accent hover:underline">
            Se connecter
          </Link>
        </p>
      </form>
    </Cadre>
  );
}
